import { type FC, useState } from 'react'

import { useMutation, useQueryClient } from 'react-query'

import { IconThreePeople } from 'assets/svg/sprite'
import InputCard from 'components/Form/InputCard/InputCard'
import { addToast } from 'components/Toast/Toast'
import { useModalContext } from 'context/modalContext'
import {
    createPromoCampaign,
    updatePromoCampaign
} from 'services/endpoints/settings/general/subscription'
import { type PromoCampaign } from 'types/api/subscription'
import { PromoCampaignType } from 'types/enums'

import style from './_add-trial-link.module.scss'

const AddTrialLink: FC<{
    campaign?: any
    isEdit?: boolean
}> = ({ campaign, isEdit }) => {
    const [selectOpen, setSelectOpen] = useState(false)
    const [activeCategory, setActiveCategory] = useState<string>(
        campaign?.audience || 'new'
    )
    const [duration, setDuration] = useState<number>(
        campaign?.duration ?? 7
    )

    const [length, setLength] = useState<number>(
        campaign?.length ?? 7
    )

    const [limit, setLimit] = useState<number>(
        campaign?.limit ?? 1
    )
    const modalData = useModalContext()
    const queryClient = useQueryClient()

    const options = [
        {
            value: 'new',
            label: 'New Subscribers'
        },
        {
            value: 'expired',
            label: 'Returning Subscribers'
        },
        {
            value: 'all',
            label: 'New & Expired'
        }
    ]

    const addMutation = useMutation(
        async (data: PromoCampaign) => await createPromoCampaign(data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('freeTrials')
                queryClient.invalidateQueries('promoCampaigns')
                modalData.clearModal()
            },
            onError: () => {
                addToast(
                    'error',
                    'An error occured, please try again',
                    undefined,
                    true
                )
                modalData.clearModal()
            }
        }
    )

    const editMutation = useMutation(
        async (data: PromoCampaign) =>
            await updatePromoCampaign(campaign.id, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('freeTrials')
                queryClient.invalidateQueries('promoCampaigns')
                modalData.clearModal()
            },
            onError: () => {
                addToast(
                    'error',
                    'An error occured, please try again',
                    undefined,
                    true
                )
                modalData.clearModal()
            }
        }
    )

    const isValid = () => {
        return (
            ['new', 'expired', 'all'].includes(activeCategory) &&
            Number(limit) > 0 &&
            Number(duration) > 0 &&
            Number(length) > 0
        )
    }

    const addFunction = () => {
        if (!isValid()) {
            addToast('error', 'Please fill all fields.')
            return
        }

        addMutation.mutate({
            type: PromoCampaignType.TRIAL_LINK,
            length: Number(length),
            limit: Number(limit),
            audience: activeCategory,
            duration: Number(duration),
            free: false
        })
    }
    const editFunction = () => {
        if (!isValid()) {
            addToast('error', 'Please fill all fields.')
            return
        }

        editMutation.mutate({
            type: PromoCampaignType.TRIAL_LINK,
            length: Number(length),
            limit: Number(limit),
            audience: activeCategory,
            duration: Number(duration),
            free: false
        })
    }
    return (
        <div className={style.container}>
            <div
                className={`${style.select_container} ${
                    selectOpen ? style.select_container_open : ''
                }`}
            >
                <div
                    className={style.select_selected}
                    onClick={() => {
                        setSelectOpen((prev) => !prev)
                    }}
                >
                    <div className={style.select_icon}>
                        <IconThreePeople />
                    </div>
                    {
                        options.filter(
                            (item: any) => item.value === activeCategory
                        )[0].label
                    }
                </div>

                <div
                    className={`${style.select_options} ${
                        selectOpen ? style.select_options_open : ''
                    }`}
                >
                    {options.map((option: { value: string; label: string }) => (
                        <div
                            onClick={() => {
                                setActiveCategory(option.value)
                                setSelectOpen(false)
                            }}
                            key={option.value}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            </div>

            <InputCard
                mask={`${duration} Days`}
                type='number'
                label='Free Trial Duration'
                value={duration}
                changeFn={(val: number) => {
                    setDuration(val)
                }}
                customClass={`${style.trialDurationInput} ${style.input} ${style.two_inputs_input}`}
            />
            <div className={style.two_inputs_container}>
                <InputCard
                    customClass={`${style.two_inputs_input} ${style.two_inputs_input_margin}`}
                    type='number'
                    label='Campaign Length'
                    value={length}
                    changeFn={(val: number) => {
                        setLength(val)
                    }}
                    mask={`${length} Days`}
                />
                <InputCard
                    customClass={style.two_inputs_input}
                    type="number"
                    label="Offer Limit"
                    value={limit}
                    changeFn={(val: number) => {
                        setLimit(val)
                    }}
                    mask={`${limit} ${Number(limit) === 1 ? 'Subscriber' : 'Subscribers'}`}
                />
            </div>

            <div className={style.submit_buttons_container}>
                <div
                    className={`${style.cancel_button}`}
                    onClick={() => {
                        modalData.clearModal()
                    }}
                >
                    Cancel
                </div>
                <div
                    className={`${style.submit_button} ${
                        addMutation.isLoading || editMutation.isLoading
                            ? style.submit_button_loading
                            : ''
                    }`}
                    onClick={!isEdit ? addFunction : editFunction}
                >
                    Generate
                </div>
            </div>
        </div>
    )
}

export default AddTrialLink
